import React from 'react';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/petitionDetails/petition-details.yml';

import type { Endorsement } from '#src/app/shared/components/endorsements';
import { EndorsementCard } from '#src/app/shared/components/endorsements';

import { useTracking } from '../../hooks/useTracking';

type Props = {
	showAll?: boolean;
	endorsements: readonly Endorsement[];
	petitionId: string;
	onOpenEndorsementsModal?: (endorsementId?: string) => void;
};

const EndorsementsListComponent: React.FC<Props> = ({
	showAll = false,
	endorsements,
	petitionId,
	onOpenEndorsementsModal,
}) => {
	const { translate } = useI18n();
	const { trackPetitionEndorserClick } = useTracking();

	if (endorsements.length === 0) {
		return null;
	}

	const sortedEndorsements = [...endorsements].sort((a, b) => {
		if (a.content && !b.content) return -1;
		if (!a.content && b.content) return 1;
		return 0;
	});

	const endorsementsToShow = showAll ? sortedEndorsements : sortedEndorsements.slice(0, 3);
	const handleEndorserClick = () => {
		trackPetitionEndorserClick(petitionId);
	};

	return (
		<div className="flex flex-col gap-6">
			{endorsementsToShow.map((endorsement) => (
				<EndorsementCard
					key={endorsement.id}
					endorsementId={endorsement.id}
					name={endorsement.name}
					title={endorsement.title ?? ''}
					description={endorsement.content ?? ''}
					photoUrl={endorsement.photo?.userMedium.url ?? ''}
					onReadMore={showAll || !onOpenEndorsementsModal ? undefined : () => onOpenEndorsementsModal(endorsement.id)}
					readMoreLabel={showAll ? undefined : translate('corgi.petition_details.endorsements.read_full')}
					link={endorsement.url ?? undefined}
					showFullDescription={showAll}
					handleEndorserClick={handleEndorserClick}
					descriptionLineClamp={3}
				/>
			))}
		</div>
	);
};

export const EndorsementsList = withI18nPlaceholders(I18N_PLACEHOLDERS, EndorsementsListComponent);
